import React, { useState, useEffect, useRef } from 'react';
import WOEWigDetails from './WOEWigDetails';
import WOEWigRooms from './WOEWigRooms';
import WOEPayment from './WOEPayment/WOEPayment';
import WOENavigationHeader from './WOENavigationHeader';
import DraftOrderModal from './DraftOrderModal';
import DraftQueuePanel from './DraftQueuePanel';
import './WOE.css';
import { isIsraelUser, getLocationDisplayName } from '../../utils/locationUtils';
import { getCurrentUser } from '../../utils/auth';
import { API_URL } from '../../config/api';
import { loadWOEState, getOrderBroadcastChannel, saveOrder, createAndSetNewOrder } from '../../utils/statePersistence';
import { useOrderManager } from '../hooks/useOrderManager';
import { createLogger } from '../../utils/logger';

const logger = createLogger('WOE');

function WOE() {
    // Initial state setup with persistence check

     const {
        activeOrders,
        currentOrder,
        isLoading: ordersLoading,
        createNewOrder,
        switchToOrder,
        closeOrder,
        duplicateOrderById,
        updateCurrentOrder,
        refreshOrders,
        getOrderDisplayInfo,
        hasMultipleOrders,
        currentOrderId
    } = useOrderManager();

    const [formData, setFormData] = useState(() => {
        return currentOrder?.formData || {
            orderType: '',
            orderNo: 'TEMP-' + Date.now(),
            customerName: '',
            phone: '',
            address: '',
            email: '',
            city: '',
            state: '',
            zip: '',
            moldId: '',
            ID: null 
        };
    });

    const [localCustomerId, setLocalCustomerId] = useState(() => {
        return currentOrder?.localCustomerId || null;
    });

    const [currentSlide, setCurrentSlide] = useState(() => {
        return currentOrder?.currentSlide || 1;
    });

    const [wigDetailsData, setWigDetailsData] = useState(() => {
        return currentOrder?.wigDetailsData || null;
    });

    const [wigRoomsData, setWigRoomsData] = useState(() => {
        return currentOrder?.wigRoomsData || [];
    });

    const [paymentData, setPaymentData] = useState(() => {
        return currentOrder?.paymentData || {
            payments: [{
                id: 1,
                method: 'Credit Card',
                amount: 0,
                notes: ''
            }],
            paymentSummary: {
                totalPaid: 0,
                balanceDue: 0,
                isProcessing: false,
                isComplete: false,
                invoiceNumber: ''
            }
        };
    });

    const [customerSearchResults, setCustomerSearchResults] = useState([]);
    const [showCustomerSearch, setShowCustomerSearch] = useState(false);
    

    const wigDetailsRef = useRef(null);
    const [wigDetails, setWigDetails] = useState(null);
    


    const [accessoryOptions, setAccessoryOptions] = useState([]);
    const [filteredAccessories, setFilteredAccessories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [selectedAccessories, setSelectedAccessories] = useState([]);
    const [newAccessory, setNewAccessory] = useState({
        category: '',
        description: '',
        price: ''
    });

    // AI Draft Order state
    const [showDraftModal, setShowDraftModal] = useState(false);
    const [showDraftQueue, setShowDraftQueue] = useState(false);
    const [draftPickupNote, setDraftPickupNote] = useState(null);
    const [draftBarcodeNote, setDraftBarcodeNote] = useState(null);
    const [draftRooms, setDraftRooms] = useState(null);
    const [draftStylistName, setDraftStylistName] = useState(null);
    const [draftQcNotes, setDraftQcNotes] = useState(null);
    const [activeDraftId, setActiveDraftId] = useState(null);
    const [draftServicePrice, setDraftServicePrice] = useState(null);
    const [draftWarrantyInfo, setDraftWarrantyInfo] = useState(null);

    const fetchAccessoryOptions = async () => {
        try {
            const response = await fetch(`${API_URL}/api/get-accessories`);
            if (!response.ok) {
                throw new Error(`Error fetching accessories: ${response.status}`);
            }
            const data = await response.json();
            setAccessoryOptions(data);
            setFilteredAccessories(data);
        } catch (error) {
            logger.error("Error fetching accessories:", error);
            setAccessoryOptions([]);
            setFilteredAccessories([]);
        }
    };

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        if (category === 'All') {
            setFilteredAccessories(accessoryOptions);
        } else {
            setFilteredAccessories(
                accessoryOptions.filter(accessory => accessory.Category === category)
            );
        }
    };

    const addAccessory = (accessory) => {
        const newAccessoryItem = {
            id: Date.now(),
            type: 'accessory',
            category: accessory.category || '',
            description: accessory.description || '',
            price: parseFloat(accessory.price?.replace('$', '')) || 0,
            quantity: 1
        };
        
        setSelectedAccessories(prevAccessories => [...prevAccessories, newAccessoryItem]);
        logger.debug("Added accessory to order:", newAccessoryItem);
    };

    const addCustomAccessoryToOrder = () => {
        if (!newAccessory.category || !newAccessory.description || !newAccessory.price) {
            alert('Please fill in all fields');
            return;
        }
        
        const price = parseFloat(newAccessory.price);
        if (isNaN(price) || price <= 0) {
            alert('Please enter a valid price');
            return;
        }
        
        const accessoryItem = {
            id: Date.now(),
            type: 'accessory',
            description: newAccessory.description,
            category: newAccessory.category,
            price: price,
            quantity: 1
        };
        
        setSelectedAccessories(prevAccessories => [...prevAccessories, accessoryItem]);
        
        setNewAccessory({
            category: '',
            description: '',
            price: ''
        });
        
        logger.debug("Added custom accessory to order:", accessoryItem);
    };

    const removeAccessory = (accessoryId) => {
        setSelectedAccessories(prevAccessories => 
            prevAccessories.filter(accessory => accessory.id !== accessoryId)
        );
    };

    const updateAccessoryQuantity = (accessoryId, newQuantity) => {
        if (newQuantity < 1) return;
        
        setSelectedAccessories(prevAccessories => 
            prevAccessories.map(accessory => 
                accessory.id === accessoryId ? { ...accessory, quantity: newQuantity } : accessory
            )
        );
    };

    const calculateAccessoryTotal = () => {
        return selectedAccessories.reduce(
            (sum, accessory) => sum + (accessory.price * accessory.quantity), 0
        );
    };

    const [isNewCustomer, setIsNewCustomer] = useState(false);

    const updateWigDetailsData = (newData) => {
        setWigDetailsData(newData);
    };

    const updateWigRoomsData = (newData) => {
        setWigRoomsData(Array.isArray(newData.wigRoomsData) ? newData.wigRoomsData : []);
        
        // Also update wigDetailsData if it changed
        if (newData.wigDetailsData) {
            setWigDetailsData(prev => ({
                ...prev,
                ...newData.wigDetailsData
            }));
        }
    };


  
    const updatePaymentData = (newData) => {
        const updatedPaymentData = newData.paymentData || paymentData;
        setPaymentData(updatedPaymentData);

        // Also update other data if provided
        if (newData.wigRoomsData) {
            setWigRoomsData(Array.isArray(newData.wigRoomsData) ? newData.wigRoomsData : []);
        }

        // 🔧 FIX: Save payment data to current order immediately
        // This is called by WOEPayment's onStateChange callback (with 500ms debounce there)
        if (currentOrder) {
            updateCurrentOrder({
                paymentData: updatedPaymentData,
                wigRoomsData: newData.wigRoomsData || wigRoomsData
            });
            // logger.debug('💾 Payment data saved from WOEPayment callback', {
            //     orderId: currentOrder.id,
            //     agreementSigned: updatedPaymentData?.agreementState?.isSigned,
            //     agreementTimestamp: updatedPaymentData?.agreementState?.timestamp,
            //     hasPaymentData: !!updatedPaymentData
            // });
        } else {
            logger.warn('⚠️ Cannot save payment data - no current order!');
        }
    };
        
        // Sync local state when current order changes (order switching)
useEffect(() => {
    if (currentOrder) {
        logger.debug('🔄 Syncing local state with current order:', currentOrder.id);
        setFormData(currentOrder.formData || {
            orderType: '',
            orderNo: 'TEMP-' + Date.now(),
            customerName: '',
            phone: '',
            address: '',
            email: '',
            city: '',
            state: '',
            zip: '',
            moldId: '',
            ID: null 
        });
        setLocalCustomerId(currentOrder.localCustomerId || null);
        setCurrentSlide(currentOrder.currentSlide || 1);
        setWigDetailsData(currentOrder.wigDetailsData || null);
        setWigRoomsData(currentOrder.wigRoomsData || []);

        // 🔧 FIX: Preserve agreementState when restoring paymentData
        const restoredPaymentData = currentOrder.paymentData || {
            payments: [{
                id: 1,
                method: 'Credit Card',
                amount: 0,
                notes: ''
            }],
            paymentSummary: {
                totalPaid: 0,
                balanceDue: 0,
                isProcessing: false,
                isComplete: false,
                invoiceNumber: ''
            }
        };

        logger.debug('🔄 Restoring payment data for order:', currentOrder.id, {
            hasAgreementState: !!restoredPaymentData.agreementState,
            agreementSigned: restoredPaymentData.agreementState?.isSigned,
            fullPaymentData: restoredPaymentData
        });

        setPaymentData(restoredPaymentData);
    }
}, [currentOrder?.id]); // Only trigger when order ID changes

// Save local state back to current order when data changes
useEffect(() => {
    if (currentOrder) {
        // 🔧 CRITICAL FIX: When on payment page (slide 4), DON'T auto-save paymentData here
        // WOEPayment has its own callback that saves paymentData with agreementState intact
        // This general auto-save was overwriting agreementState because local paymentData state
        // doesn't get updated immediately when agreementState changes in WOEPayment

        const isOnPaymentPage = currentSlide === 4;

        const updatedData = {
            formData,
            localCustomerId,
            currentSlide,
            wigDetailsData,
            wigRoomsData,
            // Only include paymentData if NOT on payment page, or merge carefully
            ...(isOnPaymentPage ? {
                // On payment page: preserve whatever is in currentOrder.paymentData
                // Don't overwrite it with local state that might be stale
                paymentData: currentOrder.paymentData
            } : {
                // Not on payment page: safe to use local paymentData
                paymentData: paymentData
            }),
        };

        const timeoutId = setTimeout(() => {
            updateCurrentOrder(updatedData);
            logger.debug('💾 Local state saved to current order (general auto-save)', {
                currentSlide,
                isOnPaymentPage,
                hasAgreementState: !!updatedData.paymentData?.agreementState,
                agreementSigned: updatedData.paymentData?.agreementState?.isSigned,
                orderId: currentOrder.id
            });
        }, 1000); // Increased from 500ms to 1000ms to reduce frequency

        return () => clearTimeout(timeoutId);
    }
}, [
    // Remove the problematic dependencies that change too frequently
    formData.customerName, // Only track customer name changes, not all formData
    formData.orderType,    // Only track order type changes
    localCustomerId,
    currentSlide,
    // wigDetailsData, // Comment out if this also causes issues
    // wigRoomsData,   // Remove this - it was causing the calendar issue
    paymentData.paymentSummary?.isComplete,
    currentOrder?.id
]); // Removed updateCurrentOrder from dependencies



useEffect(() => {
    if (!ordersLoading && activeOrders.length === 0) {
        logger.info('🆕 No orders found, creating initial order...');
        createNewOrder();
    }
}, [ordersLoading, activeOrders.length, createNewOrder]);

// 🔧 CRITICAL: Listen for ORDER_DELETED broadcasts from other tabs
// This prevents users from submitting the same order multiple times across tabs
useEffect(() => {
    const channel = getOrderBroadcastChannel();
    if (!channel) return;

    const handleBroadcast = (event) => {
        const { type, orderId, isSubmission } = event.data;

        // Check if the deleted order is our current order
        if (type === 'ORDER_DELETED' && currentOrder && orderId === currentOrder.id) {
            logger.warn(`⚠️ Current order ${orderId} was ${isSubmission ? 'submitted' : 'deleted'} in another tab`);

            // if (isSubmission) {
            //     // Order was submitted in another tab - show alert
            //     alert('This order has already been submitted in another tab/window.');
            // } else {
            //     // Order was manually closed in another tab
            //     alert('This order was closed in another tab/window.');
            // }

            // Don't create a new order - useOrderManager's refreshOrders() will handle
            // switching to another order, and the useEffect watching activeOrders.length
            // will create a new order only if there are no orders left
        }
    };

    channel.addEventListener('message', handleBroadcast);

    return () => {
        channel.removeEventListener('message', handleBroadcast);
    };
}, [currentOrder?.id]);

// Save wig details changes (with longer delay to prevent conflicts)
useEffect(() => {
    if (currentOrder && wigDetailsData) {
        const timeoutId = setTimeout(() => {
            // Only save if this wigDetailsData actually belongs to current order
            if (currentOrder.wigDetailsData !== wigDetailsData) {
                updateCurrentOrder({ wigDetailsData });
                logger.debug('💾 Wig details saved');
            }
        }, 2000);

        return () => clearTimeout(timeoutId);
    }
}, [wigDetailsData, currentOrder?.id, updateCurrentOrder]);


useEffect(() => {
    if (currentOrder && formData) {
        const timeoutId = setTimeout(() => {
            // Only save if formData has actually changed from what's stored
            const hasChanged = currentOrder.formData?.phone !== formData.phone ||
                              currentOrder.formData?.email !== formData.email ||
                              currentOrder.formData?.address !== formData.address ||
                              currentOrder.formData?.city !== formData.city ||
                              currentOrder.formData?.state !== formData.state ||
                              currentOrder.formData?.zip !== formData.zip ||
                              currentOrder.formData?.moldId !== formData.moldId;

            if (hasChanged) {
                updateCurrentOrder({ formData });
                logger.debug('💾 Form data saved');
            }
        }, 1500);

        return () => clearTimeout(timeoutId);
    }
}, [formData.phone, formData.email, formData.address, formData.city, formData.state, formData.zip, formData.moldId, currentOrder?.id, updateCurrentOrder]);



    const handleNavigationChange = (targetSlide) => {
    // 🔧 NEW: Validate forward navigation requirements
    if (targetSlide > currentSlide) {
        // Validate each step before allowing forward navigation
        if (targetSlide >= 2) {
            // Must have customer name and order type for slide 2+
            if (!formData.customerName || !formData.orderType) {
                alert('⚠️ Please enter customer name and select order type first.');
                return;
            }
        }

        if (targetSlide >= 3) {
            // Must have selected items for slide 3+ (except Accessory orders)
            if (formData.orderType !== 'Accessory') {
                const hasItems = wigDetailsData?.selectedItems?.length > 0;
                if (!hasItems) {
                    alert('⚠️ Please select at least one wig item before proceeding to room tasks.');
                    return;
                }
            }
        }

        if (targetSlide >= 4) {
            // Must have room tasks for slide 4
            if (formData.orderType !== 'Accessory' && (!wigRoomsData || wigRoomsData.length === 0)) {
                alert('⚠️ Please add at least one room task before proceeding to payment.');
                return;
            }
        }
    }

    if (canNavigateToSlide(targetSlide)) {
        logger.debug(`✅ Navigation to slide ${targetSlide} allowed`);

        // 🔧 CRITICAL FIX: Use currentOrder.paymentData when on payment page to preserve agreementState
        const isOnPaymentPage = currentSlide === 4;
        const paymentDataToSave = isOnPaymentPage ? currentOrder.paymentData : paymentData;

        // 🔧 Immediately save current state before switching
        logger.debug('💾 Saving state before header navigation...', {
            currentSlide,
            targetSlide,
            isOnPaymentPage,
            hasAgreementState: !!paymentDataToSave?.agreementState,
            agreementSigned: paymentDataToSave?.agreementState?.isSigned
        });

        updateCurrentOrder({
            formData,
            localCustomerId,
            currentSlide,
            wigDetailsData,
            wigRoomsData,
            paymentData: paymentDataToSave,
        });

        setCurrentSlide(targetSlide);
    }
    };
        
    const canNavigateToSlide = (slideId) => {
    return true;
};

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'customerName') {
            setFormData({ ...formData, [name]: value, ID: null });
            
            if (value.length > 1) {
                fetchCustomers(value);
                setShowCustomerSearch(true);
            } else {
                setShowCustomerSearch(false);
                setCustomerSearchResults([]);
                setIsNewCustomer(false);
            }
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };
    
    useEffect(() => {
        if (formData.customerName.length > 1 && showCustomerSearch) {
            // Check if the typed name matches any existing customers
            const exactMatch = customerSearchResults.some(customer => {
                const fullName = customer.CustomerFullName || `${customer.CustomerFirstName} ${customer.CustomerLastName}`;
                return fullName.toLowerCase() === formData.customerName.toLowerCase();
            });
            
            // If no exact match and we have search results or no results, it's likely a new customer
            setIsNewCustomer(!exactMatch && customerSearchResults.length === 0);
        } else {
            setIsNewCustomer(false);
        }
    }, [customerSearchResults, formData.customerName, showCustomerSearch]);

    const saveCustomerLocally = async (customerData) => {
        try {
          logger.info('💾 Saving customer to local DB...', customerData.ID ? 'UPDATE' : 'CREATE');
          
          // Parse the customer name
        const nameParts = (customerData.customerName || '').trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

            // Clean phone number consistently  
            const cleanPhone = (customerData.phone || '').replace(/\D/g, '');
          
          // Parse address if it exists
          const addressParts = customerData.address ? customerData.address.split(',') : [];
          const street = addressParts[0]?.trim() || '';
          const cityStateZip = addressParts[1]?.trim() || '';
          const cityStateParts = cityStateZip.split(' ');
          const zip = cityStateParts.pop() || customerData.zip || '';
          const state = cityStateParts.pop() || customerData.state || '';
          const city = cityStateParts.join(' ') || customerData.city || '';
          
          const requestBody = {
            CustomerFirstName: firstName,
            CustomerLastName: lastName,
            CustomerPhone: cleanPhone,  // Changed from customerData.phone
            CustomerEmail: customerData.email || '',
            CustomerAddress: street,
            CustomerCity: city,
            CustomerState: state,
            CustomerZipCode: zip,
            MoldId: customerData.moldId || '',
        };
          
          // 🆕 NEW: Include ID if updating existing customer
          if (customerData.ID) {
            requestBody.ID = customerData.ID;
          }
          
          logger.debugSafe('📤 Sending to API:', requestBody);
      
          const response = await fetch(`${API_URL}/api/customers`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });
      
          if (!response.ok) {
            throw new Error(`Failed to save customer: ${response.status}`);
          }
      
          const result = await response.json();
          logger.info('✅ Customer saved locally:', { customerId: result.customerId });
          
          return {
            success: true,
            customerId: result.customerId,
            isNew: result.isNewCustomer,
            action: result.isNewCustomer ? 'created' : 'updated'
          };
          
        } catch (error) {
          logger.error('❌ Failed to save customer locally:', error);
          return {
            success: false,
            error: error.message
          };
        }
      };
      
      
  
    const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.orderType || !formData.customerName) {
        alert('Please fill in all required fields (Order Type and Customer Name)');
        return;
    }

    // For Accessory orders, require at least one accessory
    if (formData.orderType === 'Accessory' && selectedAccessories.length === 0) {
        alert('Please add at least one accessory to the order before continuing.');
        return;
    }

    logger.debugSafe('Customer Info Submitted', formData);

    const localSaveResult = await saveCustomerLocally(formData);

    if (!localSaveResult.success) {
        alert(`❌ Failed to save customer: ${localSaveResult.error}\n\nPlease fill out phone number and address fields. Phone number must not be associated with another customer.`);
        return;
    }

    logger.info('✅ Customer saved locally', { customerId: localSaveResult.customerId });
    setLocalCustomerId(localSaveResult.customerId);

    setFormData(prev => ({
        ...prev,
        ID: localSaveResult.customerId,
        customerNo: localSaveResult.customerId
    }));

    if (formData.orderType === 'Accessory') {
        setCurrentSlide(4);
    } else {
        setCurrentSlide(2);
    }
};

// Load accessory options when order type is Accessory
useEffect(() => {
    if (formData.orderType === 'Accessory') {
        fetchAccessoryOptions();
    }
}, [formData.orderType]);


    const handlePrevious = (data) => {
        if (currentSlide === 3 && data) {
            // 🔧 FIX: Properly save both wigRooms and wigDetails when navigating back
            if (data.wigRoomsData) {
                setWigRoomsData(data.wigRoomsData);
            }
            if (data.wigDetailsData) {
                setWigDetailsData(data.wigDetailsData);
            }
            // Also save to order
            updateCurrentOrder({
                wigRoomsData: data.wigRoomsData,
                wigDetailsData: data.wigDetailsData
            });
        } else if (currentSlide === 4 && data) {
            // 🔧 CRITICAL FIX: Update local state first, then save
            if (data.paymentData) {
                setPaymentData(data.paymentData);
                logger.debug('💾 Saving payment data from slide 4 navigation (Previous button)', {
                    hasAgreementState: !!data.paymentData.agreementState,
                    agreementSigned: data.paymentData.agreementState?.isSigned
                });
            }
            if (data.wigRoomsData) {
                setWigRoomsData(data.wigRoomsData);
            }

            // Save to order - data.paymentData should already have agreementState from WOEPayment
            updateCurrentOrder({
                paymentData: data.paymentData, // This includes agreementState from WOEPayment
                wigRoomsData: data.wigRoomsData || wigRoomsData
            });
        }
        setCurrentSlide(currentSlide - 1);
    };

    const handleNext = (data) => {
        logger.debug("Parent received data:", data);
        if (currentSlide === 2) {
            setWigDetailsData(data);
            logger.debug("Updated wigDetailsData:", data);
            // 🔧 FIX: Save to order when moving forward
            updateCurrentOrder({ wigDetailsData: data });
        } else if (currentSlide === 3) {
            setWigRoomsData(Array.isArray(data.wigRoomsData) ? data.wigRoomsData : []);
            setWigDetailsData(prev => ({
                ...prev,
                ...data
            }));
            // 🔧 FIX: Save to order when moving forward (including appointmentId)
            console.log('🔗 WOE.jsx - Saving appointmentId from WOEWigRooms:', data.appointmentId);
            updateCurrentOrder({
                wigRoomsData: Array.isArray(data.wigRoomsData) ? data.wigRoomsData : [],
                wigDetailsData: { ...wigDetailsData, ...data },
                appointmentId: data.appointmentId || null // 🔗 Save appointment ID
            });
        } else if (currentSlide === 4) {
            setPaymentData(data.paymentData || {
                payments: [{
                    id: 1,
                    method: 'Credit Card',
                    amount: 0,
                    notes: ''
                }],
                paymentSummary: {
                    totalPaid: 0,
                    balanceDue: 0,
                    isProcessing: false,
                    isComplete: false,
                    invoiceNumber: ''
                }
            });
        }
        setCurrentSlide(currentSlide + 1);
    };

    const fetchCustomers = async (searchTerm) => {
        try {
            if (searchTerm.length < 2) {
                setCustomerSearchResults([]);
                return;
            }

            logger.debug('Searching for customers with term:', searchTerm);

            const response = await fetch(`${API_URL}/api/customers/search?term=${encodeURIComponent(searchTerm)}`);

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                logger.error('Error details:', errorData);
                throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            logger.debug('Found customers:', data.length);
            setCustomerSearchResults(Array.isArray(data) ? data : []);
        } catch (error) {
            logger.error('Error searching customers:', error);
            setCustomerSearchResults([]);
        }
    };


        const selectCustomer = (customer) => {
            setFormData({
                ...formData,
                customerName: customer.CustomerFullName || `${customer.CustomerFirstName} ${customer.CustomerLastName}`,
                phone: customer.CustomerPhone || '',
                address: customer.CustomerAddress && customer.CustomerCity && customer.CustomerState
                    ? `${customer.CustomerAddress}, ${customer.CustomerCity}, ${customer.CustomerState}`
                    : '',
                email: customer.CustomerEmail || '',
                city: customer.CustomerCity || '',
                state: customer.CustomerState || '',
                zip: customer.CustomerZip || '',
                moldId: customer.MoldId || '',
                ID: customer.ID
            });
            
            setLocalCustomerId(customer.ID);
            setShowCustomerSearch(false);
            setCustomerSearchResults([]);
            setIsNewCustomer(false);
            
        };

    const handleFinalSubmit = (data) => {
        if (data && data.orderNo) {
            alert(`Order #${data.orderNo} has been successfully submitted!`);
        } else {
            alert("Order has been successfully submitted!");
        }

        // Mark the originating draft as converted so it leaves the queue
        if (activeDraftId) {
            fetch(`${API_URL}/api/draft-orders/${activeDraftId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    status: 'converted',
                    claimedBy: getCurrentUser(),
                    orderNo: data?.orderNo || null,
                }),
            }).catch(err => logger.error('Failed to mark draft as converted:', err));
            setActiveDraftId(null);
        }

        // Mark current order as completed and remove from active orders
        if (currentOrder) {
            logger.info('🎉 Order completed, removing from active orders');
            // Pass isSubmission: true to broadcast to other tabs for immediate clearing
            closeOrder(currentOrder.id, { force: true, isSubmission: true });

            // If no more orders, create a new one
            if (activeOrders.length <= 1) {
                createNewOrder();
            }
        }
    };

    // 🔧 NEW: Wrapper for switching orders that saves current order first
    const handleSwitchOrder = (targetOrderId) => {
        if (currentOrder && targetOrderId !== currentOrderId) {
            // 🔧 CRITICAL: When on payment page, use currentOrder.paymentData instead of local paymentData
            // because WOEPayment's callback updates currentOrder.paymentData directly,
            // but local paymentData state might be stale
            const isOnPaymentPage = currentSlide === 4;
            const paymentDataToSave = isOnPaymentPage ? currentOrder.paymentData : paymentData;

            logger.debug('💾 Saving current order before switching...', {
                fromOrderId: currentOrderId,
                toOrderId: targetOrderId,
                currentSlide,
                isOnPaymentPage,
                usingCurrentOrderPaymentData: isOnPaymentPage,
                localPaymentHasAgreement: !!paymentData.agreementState,
                currentOrderHasAgreement: !!currentOrder.paymentData?.agreementState,
                agreementSigned: paymentDataToSave?.agreementState?.isSigned
            });

            // Save current state immediately before switching
            updateCurrentOrder({
                formData,
                localCustomerId,
                currentSlide,
                wigDetailsData,
                wigRoomsData,
                paymentData: paymentDataToSave,
            });

            logger.debug('✅ Current order saved, now switching to:', targetOrderId);
        }

        // Now switch to the new order
        switchToOrder(targetOrderId);
    };

    // Start a brand-new WOE order pre-seeded from an AI draft
    const handleStartFromDraft = (draft) => {
        const pj = draft.parsedJson || {};

        // 1. Create the new order in localStorage, then immediately overwrite it with
        //    draft data BEFORE refreshOrders reads it. This prevents the sync useEffect
        //    from seeing an empty wigRoomsData and wiping what we're about to set.
        const newOrder = createAndSetNewOrder(pj.orderType || '', pj.customerName || '');

        // 2. Reset ALL local WOE state to clean defaults
        setCurrentSlide(1);
        setWigDetailsData(null);
        setLocalCustomerId(null);
        setSelectedAccessories([]);
        setPaymentData({
            payments: [{ id: 1, method: 'Credit Card', amount: 0, notes: '' }],
            paymentSummary: {
                totalPaid: 0, balanceDue: 0,
                isProcessing: false, isComplete: false, invoiceNumber: ''
            }
        });

        // 3. Pre-fill slide 1 with draft customer info
        setFormData({
            orderType: pj.orderType || '',
            orderNo: 'TEMP-' + Date.now(),
            customerName: pj.customerName || '',
            phone: '',
            address: '',
            email: '',
            city: '',
            state: '',
            zip: '',
            moldId: '',
            ID: null
        });

        // 4. Reminders for slides 2 and 3 (receptionist acts on these)
        setDraftPickupNote(pj.pickupDate || null);
        setDraftBarcodeNote(pj.barcode || null);
        setDraftRooms(Array.isArray(pj.rooms) && pj.rooms.length > 0 ? pj.rooms : null);
        setDraftStylistName(pj.stylistName || null);
        setDraftQcNotes(pj.qcNotes != null ? pj.qcNotes : null);
        setActiveDraftId(draft.id || null);
        setDraftServicePrice(pj.servicePrice || null);
        setDraftWarrantyInfo(pj.warrantyInfo || null);

        // 5. Build slide 3 rooms from the draft
        const isWashAndSet = (pj.orderType || '').toLowerCase() === 'wash & set';
        const aiRooms = Array.isArray(pj.rooms) ? pj.rooms : [];

        const defaultLayout = [
            { roomNo: '6001',  roomTask: "Rachel's Room" },
            { roomNo: '1001',  roomTask: 'Build Wefts' },
            { roomNo: '1002',  roomTask: 'Machine' },
            { roomNo: '3001',  roomTask: 'HandSew' },
            { roomNo: '2002',  roomTask: 'Color' },
            { roomNo: '2003',  roomTask: 'Part & Bleach the Knots' },
            { roomNo: '1003',  roomTask: 'Comb, Clips, Band...' },
            { roomNo: '4001',  roomTask: 'Treatment' },
            { roomNo: '5001',  roomTask: 'Wash & Set' },
        ];

        const findDraftNotes = (defaultRoomTask) => {
            const key = defaultRoomTask.toLowerCase();
            const match = aiRooms.find(r => {
                const rn = (r.roomName || '').toLowerCase();
                return key.includes(rn) || rn.includes(key.split(' ')[0]);
            });
            return match?.taskNotes || '';
        };

        let mergedTasks;

        if (isWashAndSet) {
            // Wash & Set: only use the AI-mentioned rooms — don't inflate with empty defaults.
            // The normal auto-add is intentionally skipped for Wash & Set orders.
            mergedTasks = aiRooms.map((r, i) => ({
                id: Date.now() + i * 10,
                roomNo: defaultLayout.find(d => {
                    const dk = d.roomTask.toLowerCase();
                    const rk = (r.roomName || '').toLowerCase();
                    return dk.includes(rk) || rk.includes(dk.split(' ')[0]);
                })?.roomNo || '',
                roomTask: r.roomName,
                assignedWorker: '',
                taskDueDate: '',
                taskNotes: r.taskNotes || '',
                qcNotes: '',
                location: 'Pomona',
                status: 'Pending',
            }));
        } else {
            // All other order types: merge AI notes into the full default room grid
            mergedTasks = defaultLayout.map((t, i) => ({
                id: Date.now() + i * 10,
                roomNo: t.roomNo,
                roomTask: t.roomTask,
                assignedWorker: '',
                taskDueDate: '',
                taskNotes: findDraftNotes(t.roomTask),
                qcNotes: '',
                location: 'Pomona',
                status: 'Pending',
            }));

            // Prepend any AI rooms not in the default list
            const defaultKeywords = new Set(defaultLayout.map(t => t.roomTask.toLowerCase().split(' ')[0]));
            aiRooms.forEach((r, i) => {
                const rn = (r.roomName || '').toLowerCase();
                const isMatched = [...defaultKeywords].some(k => rn.includes(k) || k.includes(rn));
                if (!isMatched) {
                    mergedTasks.unshift({
                        id: Date.now() + 9000 + i,
                        roomNo: '',
                        roomTask: r.roomName,
                        assignedWorker: '',
                        taskDueDate: '',
                        taskNotes: r.taskNotes || '',
                        qcNotes: '',
                        location: 'Pomona',
                        status: 'Pending',
                    });
                }
            });
        }

        // Save rooms into localStorage BEFORE refreshOrders reads the order,
        // so the sync useEffect gets wigRoomsData already populated.
        const draftFormData = {
            orderType: pj.orderType || '',
            orderNo: newOrder?.formData?.orderNo || 'TEMP-' + Date.now(),
            customerName: pj.customerName || '',
            phone: '', address: '', email: '',
            city: '', state: '', zip: '',
            moldId: '', ID: null,
        };
        if (newOrder) {
            saveOrder({
                ...newOrder,
                formData: draftFormData,
                wigRoomsData: mergedTasks,
            });
        }

        // NOW trigger refreshOrders so the sync useEffect sees the saved rooms
        refreshOrders();

        // Also set local state directly (belt-and-suspenders)
        setWigRoomsData(mergedTasks);
        setFormData(draftFormData);
    };

    return (
        <>
        <div className="slides-container">


            {/* Navigation Header with enhanced props */}
            <WOENavigationHeader
                currentSlide={currentSlide}
                onSlideChange={handleNavigationChange}
                customerInfo={formData}
                isIsraelUser={isIsraelUser()}
                wigDetailsData={wigDetailsData}
                wigRoomsData={wigRoomsData}
                paymentData={paymentData}
                // Multi-order props
                activeOrders={activeOrders}
                currentOrderId={currentOrderId}
                onSwitchOrder={handleSwitchOrder} // 🔧 Use wrapper instead of direct switchToOrder
                onCreateOrder={createNewOrder}
                onCloseOrder={closeOrder}
                onDuplicateOrder={duplicateOrderById}
                getOrderDisplayInfo={getOrderDisplayInfo}
            />
            {currentSlide === 1 && (
                <div className={`slide ${currentSlide === 1 ? 'active' : ''}`}>
                    <div className="woe-container">
                        <h2>Work Order Entry</h2>

                        <form onSubmit={handleSubmit} className="order-form">
                            <div className="form-group no-flex">
                                <label htmlFor="orderNo">Order No:</label>
                                <input
                                    type="text"
                                    id="orderNo"
                                    name="orderNo"
                                    value={formData.orderNo}
                                    readOnly
                                />
                            </div>
                            
                            <div className="form-group no-flex">
                                <label htmlFor="orderType">Order Type: *</label>
                                <select
                                    id="orderType"
                                    name="orderType"
                                    
                                    value={formData.orderType}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Order Type</option>
                                    <option value="New Wig">New Wig</option>
                                    <option value="New Sale Wig">New Sale Wig</option>
                                    <option value="Aidel New Wig">Aidel New Wig</option>
                                    <option value="Wash & Set">Wash & Set</option>
                                    <option value="Wig Repair">Wig Repair</option>
                                    <option value="Aidel Wig Repair">Aidel Wig Repair</option>
                                    <option value="Consultation">Consultation</option>
                                    <option value="Accessory">Accessory</option>
                                    <option value="Recut">Recut</option>
                                </select>
                            </div>

                            <div className="form-group no-flex">
                                <label htmlFor="customerName">Customer Name: *</label>
                                <input
                                    type="text"
                                    id="customerName"
                                    name="customerName"
                                    value={formData.customerName}
                                    onChange={handleChange}
                                    autoComplete="off"
                                    required
                                />
                                {isNewCustomer && formData.customerName.length > 1 && (
                                    <div className="new-customer-notice">
                                        <span className="new-customer-icon">✨</span>
                                        <span className="new-customer-text">
                                            <strong>New Customer</strong> - Will be created when you continue
                                        </span>
                                    </div>
                                )}
                                {/* Customer search results dropdown */}
                                {showCustomerSearch && customerSearchResults.length > 0 && (
                                    <div className="customer-search-results">
                                        {customerSearchResults.map((customer) => (
                                            <div
                                                key={customer.ID}
                                                className="customer-search-item"
                                                onClick={() => selectCustomer(customer)}
                                            >
                                                {customer.CustomerFullName || `${customer.CustomerFirstName} ${customer.CustomerLastName}`}
                                                <span className="customer-detail">
                                                    {customer.CustomerPhone && `📞 ${customer.CustomerPhone}`}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="form-group no-flex">
                                <label htmlFor="phone">Phone:</label>
                                <input
                                    type="text"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-group no-flex">
                                <label htmlFor="address">Address:</label>
                                <input
                                    type="text"
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder="Street, City, State"
                                />
                            </div>

                            <div className="form-group no-flex">
                                <label htmlFor="email">Email:</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group no-flex">
                                <label htmlFor="moldId">Mold ID:</label>
                                <input
                                    type="text"
                                    id="moldId"
                                    name="moldId"
                                    value={formData.moldId}
                                    onChange={handleChange}
                                    placeholder="Optional - Enter Mold ID"
                                />
                            </div>

                            {/* Accessory Selection Section - Only show for Accessory orders */}
{formData.orderType === 'Accessory' && (
    <div className="accessory-selection-section">
        <h3>Select Accessories</h3>

           {/* Add new accessory form */}
            <div className="add-accessory-form">
                <h4>Add Custom Accessory</h4>
                <table>
                    <tbody>
                        <tr>
                            <td>
                                <label>Category</label>
                                <select
                                    value={newAccessory.category}
                                    onChange={(e) => setNewAccessory({...newAccessory, category: e.target.value})}
                                >
                                    <option value="">Select Category</option>
                                    <option value="Accessories">Accessories</option>
                                    <option value="Wig Heads">Wig Heads</option>
                                    <option value="Wig Boxes">Wig Boxes</option>
                                    <option value="Teeny Dini">Teeny Dini</option>
                                    <option value="Bands">Bands</option>
                                </select>
                            </td>
                            <td>
                                <label>Description</label>
                                <input 
                                    type="text"
                                    value={newAccessory.description}
                                    onChange={(e) => setNewAccessory({...newAccessory, description: e.target.value})}
                                />
                            </td>
                            <td>
                                <label>Price</label>
                                <input 
                                    type="number" 
                                    step="0.01"
                                    value={newAccessory.price}
                                    onChange={(e) => setNewAccessory({...newAccessory, price: e.target.value})}
                                />
                            </td>
                            <td>
                                <button 
                                    type="button"
                                    onClick={addCustomAccessoryToOrder}
                                >
                                    Add to Order
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        
        <div className="category-filter">
            <label>Filter By Category:</label>
            <select 
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
            >
                <option value="All">All</option>
                <option value="Accessories">Accessories</option>
                <option value="Wig Heads">Wig Heads</option>
                <option value="Wig Boxes">Wig Boxes</option>
                <option value="Teeny Dini">Teeny Dini</option>
                <option value="Bands">Bands</option>
            </select>
        </div>
        
        <div className="accessories-table-container">
            <table className="accessories-table">
                <thead>
                    <tr>
                        <th>Action</th>
                        <th>Category</th>
                        <th>Description</th>
                        <th>Price</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredAccessories.length > 0 ? (
                        filteredAccessories.map((accessory, index) => (
                            <tr key={index}>
                                <td>
                                    <button 
                                        type="button" 
                                        onClick={() => addAccessory({
                                            category: accessory.Category,
                                            description: accessory.Accessory_Description,
                                            price: `$${accessory.Retail_Price.toFixed(2)}`
                                        })}
                                    >
                                        Add
                                    </button>
                                </td>
                                <td>{accessory.Category}</td>
                                <td>{accessory.Accessory_Description}</td>
                                <td>${accessory.Retail_Price.toFixed(2)}</td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="4">Loading accessories...</td>
                        </tr>
                    )}
                </tbody>
            </table>
            
         
        </div>

        {/* Selected Accessories Summary */}
        <div className="selected-accessories-section">
            <h4>Selected Accessories</h4>
            
            {selectedAccessories.length > 0 ? (
                <div className="accessories-summary">
                    <table className="summary-table">
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>Description</th>
                                <th>Quantity</th>
                                <th>Price</th>
                                <th>Subtotal</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedAccessories.map(accessory => (
                                <tr key={accessory.id}>
                                    <td>{accessory.category}</td>
                                    <td>{accessory.description}</td>
                                    <td>
                                        <input 
                                            type="number" 
                                            value={accessory.quantity}
                                            onChange={(e) => updateAccessoryQuantity(accessory.id, parseInt(e.target.value))}
                                            min="1"
                                            style={{ width: '60px' }}
                                        />
                                    </td>
                                    <td>${accessory.price.toFixed(2)}</td>
                                    <td>${(accessory.price * accessory.quantity).toFixed(2)}</td>
                                    <td>
                                        <button 
                                            type="button" 
                                            className="remove-button"
                                            onClick={() => removeAccessory(accessory.id)}
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    <div className="total-summary">
                        <strong>Accessories Total: ${calculateAccessoryTotal().toFixed(2)}</strong>
                    </div>
                </div>
            ) : (
                <p>No accessories selected yet.</p>
            )}
        </div>
    </div>
)}


                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={
                                    !formData.orderType ||
                                    !formData.customerName ||
                                    (formData.orderType === 'Accessory' && selectedAccessories.length === 0)
                                }
                            >
                                {formData.orderType === 'Accessory' ? 'Continue to Payment' : 'Continue to Wig Details'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {currentSlide === 2 && (
                <div className={`slide ${currentSlide === 2 ? 'active' : ''}`}>
                    <WOEWigDetails
                    customerInfo={{
                        ...formData,
                        customerNo: formData.ID || localCustomerId,
                        ID: formData.ID || localCustomerId
                    }}
                    savedWigData={wigDetailsData}
                    draftBarcodeNote={draftBarcodeNote}
                    draftServicePrice={draftServicePrice}
                    draftWarrantyInfo={draftWarrantyInfo}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    onStateChange={updateWigDetailsData} // Add this new prop
                    />
                </div>
                )}

            {currentSlide === 3 && (
                <div className={`slide ${currentSlide === 3 ? 'active' : ''}`}>
                    <WOEWigRooms
                        customerInfo={{
                            ...formData,
                            customerNo: formData.ID || localCustomerId,
                            ID: formData.ID || localCustomerId
                        }}
                        wigDetails={wigDetailsData?.wigData}
                        orderItems={wigDetailsData?.selectedItems || []}
                        accessories={wigDetailsData?.selectedAccessories || []}
                        barcode={wigDetailsData?.barcode}
                        serialCode={wigDetailsData?.serialCode}
                        orderNo={formData.orderNo}
                        wigRoomsData={wigRoomsData}
                        draftPickupNote={draftPickupNote}
                        draftRooms={draftRooms}
                        draftStylistName={draftStylistName}
                        draftQcNotes={draftQcNotes}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                        onStateChange={updateWigRoomsData} // ✅ ADD THIS LINE - was missing!
                    />
                </div>
            )}

            {currentSlide === 4 && (
                <div className={`slide ${currentSlide === 4 ? 'active' : ''}`}>
                    <WOEPayment
                        customerInfo={{
                            ...formData,
                            customerNo: formData.ID || localCustomerId,
                            ID: formData.ID || localCustomerId
                        }}
                        wigDetails={wigDetailsData?.wigData}
                        wigData={wigDetailsData?.wigData}
                        wigRoomsData={wigRoomsData}
                        orderItems={wigDetailsData?.selectedItems || []}
                        accessories={formData.orderType === 'Accessory' ? selectedAccessories : (wigDetailsData?.selectedAccessories || [])}
                        barcode={wigDetailsData?.barcode}
                        serialCode={wigDetailsData?.serialCode}
                        orderNo={formData.orderNo}
                        savedPaymentData={paymentData}
                        onOrderNoUpdate={(newOrderNo) => setFormData(prev => ({ ...prev, orderNo: newOrderNo }))}
                        onPrevious={handlePrevious}
                        onSubmit={handleFinalSubmit}
                        onStateChange={updatePaymentData}
                        appointmentId={currentOrder?.appointmentId || null}
                        currentOrderId={currentOrderId}
                    />
                </div>
            )}
        </div>

        {/* ── AI Assistant floating buttons (outside slides-container to escape overflow:hidden) ── */}
        <div style={{
            position: 'fixed', bottom: 204, right: 24,
            display: 'flex', flexDirection: 'column', gap: 10,
            zIndex: 999,
        }}>
            <button
                onClick={() => setShowDraftQueue(true)}
                title="View draft orders from stylists"
                style={fabStyle('#6366f1')}
            >
                📋
            </button>
            <button
                onClick={() => setShowDraftModal(true)}
                title="AI Order Assistant — speak your order"
                style={fabStyle('#1a1a2e')}
            >
                🎙
            </button>
        </div>

        {/* ── AI Draft Order Modal ────────────────────────────────────── */}
        <DraftOrderModal
            isOpen={showDraftModal}
            onClose={() => setShowDraftModal(false)}
            currentUser={getCurrentUser()}
        />

        {/* ── Draft Queue Panel ───────────────────────────────────────── */}
        <DraftQueuePanel
            isOpen={showDraftQueue}
            onClose={() => setShowDraftQueue(false)}
            currentUser={getCurrentUser()}
            onStartOrder={handleStartFromDraft}
        />
        </>
    );
}

function fabStyle(bg) {
    return {
        width: 52, height: 52,
        borderRadius: '50%',
        background: bg, color: '#fff',
        border: 'none', fontSize: 22,
        cursor: 'pointer',
        boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    };
}

export default WOE;